/**
 * 기초생활수급자 장례 혜택 랜딩(구 실험 #327 B, 상시 채택) 콘텐츠 데이터.
 * design_handoff_recipient_funeral_funnel README(hifi 스펙) 기준.
 */
import { LeadUrgencyStatus } from "@/apis/postLeadV2";

export const RECIPIENT_HERO = {
  title: "기초생활수급자 장례",
  subtext: "받을 수 있는 장례 혜택과 예상 비용을\n1분 만에 확인해보세요",
};

/** 절감 하이라이트 박스. "100~500만 원"만 강조. */
export const RECIPIENT_HIGHLIGHT = {
  prefix: "기초생활수급자의 경우 장례 전체 비용을 ",
  emphasis: "100~500만 원",
  suffix: "까지 더 줄일 수 있어요.",
};

/** 섹션 1 — 혜택 목록 */
export const RECIPIENT_BENEFIT = {
  num: "1",
  title: "다음의 혜택을 받을 수 있어요",
};

export const RECIPIENT_BENEFIT_ITEMS: { title: string; desc?: string }[] = [
  { title: "장제 급여" },
  { title: "화장장 사용료 감면", desc: "화장장에 따라 이용 시간이 제한될 수 있어요" },
  { title: "시립 장지", desc: "지자체마다 안치 가능 여부와 조건이 달라요" },
  { title: "상조 할인", desc: "고이에서 수급자 감면을 적용해요" },
];

/** 섹션 2 — 무빈소 비용 비교 */
export const RECIPIENT_COMPARE = {
  num: "2",
  title: "무빈소로 진행하면\n비용을 크게 줄일 수 있어요",
  // "300~500만 원"만 형광펜 하이라이트
  lead: {
    prefix: "빈소를 차리는 장례와 비교하면 상황에 따라 약 ",
    emphasis: "300~500만 원",
    suffix: "까지 비용을 줄일 수 있어요.",
  },
  caption: "무빈소장은 빈소를 마련하지 않아 빈소 대실료와 음식·접객 비용이 들지 않아요.",
  defTitle: "무빈소란?",
  defBody:
    "빈소와 조문 절차 없이 안치 → 입관 → 화장 → 장지 순으로 진행하는 장례 방식이에요.\n최근에는 정해진 장례 형식에서 벗어나, 가족끼리 고인을 추모하는 무빈소장을 선택하는 분들도 많아지고 있어요.",
};

/** 비교 막대그래프 컬럼. heightPct는 부모(190px) 대비 비율. */
export const RECIPIENT_COMPARE_BARS: {
  key: string;
  priceLabel: string;
  heightPct: number;
  isPrimary: boolean;
  bottomLabel: string;
}[] = [
  {
    key: "mubinso",
    priceLabel: "200~300만 원",
    heightPct: 38,
    isPrimary: true,
    bottomLabel: "무빈소",
  },
  {
    key: "binso",
    priceLabel: "400~800만 원",
    heightPct: 88,
    isPrimary: false,
    bottomLabel: "빈소 대실",
  },
];

/** 폼 섹션 */
export const RECIPIENT_FORM = {
  title: "장례 혜택과 총비용 알아보기",
  // "비용 절감 방법"만 밑줄 강조
  subtext: {
    prefix: "지역마다 혜택 범위가 달라요. 상황을 입력하시면 ",
    underline: "비용 절감 방법",
    suffix: "을 바로 정리해드려요.",
  },
  regionLabel: "어디에서 모실 계획이신가요?",
  statusLabel: "장례 준비 상황을 알려주세요",
  inlineCta: "무료로 안내받기",
  inlineCtaSub: "필요하신 정보를 모두 안내드릴게요",
};

export const RECIPIENT_FIXED_CTA = "수급자 장례 혜택과 예상 비용 알아보기";

/** 바텀시트 */
export const RECIPIENT_SHEET = {
  title: "내 조건에 맞는 내용을 안내해드릴게요",
  titleFree: " (무료)",
  desc: "입력하신 지역과 준비 상황을 바탕으로\n받을 수 있는 혜택과 장례 예상 비용을 전달드려요.",
  phonePlaceholder: "010-0000-0000",
  consentRequired: "[필수]",
  consentLabel: "개인정보 수집·이용·제공 동의",
  // 야간(useGetServerTime().is_night)에만 노출되는 긴급 수신 동의. 무빈소 리드 모달과 동일한 고지.
  nightConsentRequired: "[필수]",
  nightConsentLabel: "긴급 상황 시 수신 동의 (야간)",
  // 버튼 문구에 동의 명시("제출=동의"). 무빈소 리드 모달과 동일한 방식.
  submit: "동의하고 무료로 안내받기",
};

/** 완료 화면 */
export const RECIPIENT_DONE = {
  title: "접수됐어요",
  body: "입력하신 번호로 혜택과 예상 비용 안내를 보내드릴게요.",
};

/**
 * 준비 상황 텍스트 → postLeadV2 status(LeadUrgencyStatus) 매핑.
 * ⚠️ 인덱스 아닌 텍스트 키(order-safe). 공유 상수 장례준비긴급도_LIST의 순서가 바뀌어도 안전하고,
 *    문구가 바뀌면 키 미스매치로 undefined(=잘못된 status 대신 안전한 누락)가 된다. (#310 order-safe 원칙)
 */
export const RECIPIENT_URGENCY_MAP: Record<string, LeadUrgencyStatus> = {
  "급하지 않지만 미리 알아보려고 해요.": "여유",
  "1주에서 한 달 정도 기간이 남은 것 같아요.": "1달",
  "임종이 며칠 남지 않았어요.": "임박",
  "임종하신 상태입니다.": "임종상태",
};
